<script setup lang="ts">
import { computed } from 'vue';
import type { UsageData } from '../api/types';
import { formatNumber, formatPercent } from '../utils/format';

interface Props {
  readonly data: UsageData;
}
const props = defineProps<Props>();

/**
 * The "总剩余" number is computed across ALL providers (including disabled
 * ones, which contribute 0 because they have no windows). We surface both
 * the raw scalar and a per-providers-aware percent of how much is left.
 */
const aggregate = computed(() => {
  let used = 0;
  let total = 0;
  let okCount = 0;
  for (const provider of props.data.providers) {
    if (!provider.enabled) continue;
    for (const win of provider.windows) {
      // Skip wins with no total — they represent infinite / N/A budgets.
      if (!Number.isFinite(win.total) || win.total <= 0) continue;
      used += Number.isFinite(win.used) ? win.used : 0;
      total += win.total;
      if (provider.ok) okCount += 1;
    }
  }
  const ratio = total > 0 ? used / total : 0;
  return {
    remaining: Math.max(total - used, 0),
    used,
    total,
    ratio,
    okCount,
  };
});

const usedPercent = computed(() => formatPercent(aggregate.value.ratio));
const enabledCount = computed(() => props.data.summary.enabled);
const totalCount = computed(() => props.data.summary.total);
const errorCount = computed(() => props.data.summary.errors);

function usedClass(): string {
  const r = aggregate.value.ratio;
  if (r >= 0.85) return 'summary-card--danger';
  if (r >= 0.6) return 'summary-card--warning';
  return 'summary-card--success';
}

function errorClass(): string {
  if (errorCount.value === 0) return 'summary-card--success';
  if (errorCount.value >= totalCount.value / 2) return 'summary-card--danger';
  return 'summary-card--warning';
}
</script>

<template>
  <section
    class="summary-grid"
    aria-label="汇总指标"
  >
    <article class="summary-card summary-card--accent">
      <div class="summary-card__label">总剩余</div>
      <div class="summary-card__value">
        {{ formatNumber(aggregate.remaining) }}
      </div>
      <div class="summary-card__hint">
        of {{ formatNumber(aggregate.total) }} ({{ usedPercent === '—' ? '—' : `已用 ${usedPercent}` }})
      </div>
    </article>

    <article class="summary-card">
      <div class="summary-card__label">已用</div>
      <div class="summary-card__value">
        {{ formatNumber(aggregate.used) }}
      </div>
      <div class="summary-card__hint">
        已配置平台共 {{ formatNumber(aggregate.total) }} 单位
      </div>
    </article>

    <article :class="['summary-card', usedClass()]">
      <div class="summary-card__label">使用率</div>
      <div class="summary-card__value">{{ usedPercent }}</div>
      <div class="summary-card__hint">
        ok 平台 {{ aggregate.okCount }} / {{ enabledCount }}
      </div>
    </article>

    <article :class="['summary-card', errorClass()]">
      <div class="summary-card__label">平台</div>
      <div class="summary-card__value">
        {{ enabledCount }}<span style="opacity: 0.4; font-size: 16px;"> / {{ totalCount }}</span>
      </div>
      <div class="summary-card__hint">
        <template v-if="errorCount === 0">全部正常</template>
        <template v-else>{{ errorCount }} 个错误</template>
      </div>
    </article>
  </section>
</template>
